// feel-so-good-dashboard が流通させるイベント型 (spec2.md 準拠)。
//
// 1 raw_data に対して、最低 1 件の RawDataUpload が出る。
// その後、ステータス遷移ごとに以下のいずれか 1 件が出る (BE 側で確定後):
//   - RawDataConversionSucceeded  (= 緑)
//   - RawData.JobFailed           (= 赤)
//   - RawData.JobCancelled        (= 灰)
//   - RawData.NoTarget            (= 青灰, 読取り対象が 1 件も無かった。BE の NO_TARGET)
// まだ PROCESSING 中の raw_data は RawDataUpload のみが流れる (= 黄)。
//
// イベント形式は { id, timestamp, eventName, metadata } の判別ユニオン。
// id は event の固有 UUID。raw_data の同一性は metadata.rawDataId で串刺しする。

export type EventName =
  | 'RawDataUpload'
  | 'RawDataConversionSucceeded'
  | 'RawData.JobFailed'
  | 'RawData.JobCancelled'
  | 'RawData.NoTarget';

export interface EventMetadataBase {
  readonly companyId: string;
  readonly companyName: string;
  readonly rawDataId: string;
  readonly jobId?: string;
  /**
   * route-D 社内 (検証・顧客環境立ち上げ用) の会社のイベントなら true。顧客のイベントでは付かない。
   * サーバが env INTERNAL_COMPANY_IDS と companyId を突き合わせて付ける (SQL / events.json には無い)。
   */
  readonly isInternal?: true;
}

export interface RawDataUploadEvent {
  readonly id: string;
  readonly timestamp: string; // ISO 8601
  readonly eventName: 'RawDataUpload';
  readonly metadata: EventMetadataBase & { readonly documentName: string };
}

export interface RawDataConversionSucceededEvent {
  readonly id: string;
  readonly timestamp: string;
  readonly eventName: 'RawDataConversionSucceeded';
  readonly metadata: EventMetadataBase;
}

export interface RawDataJobFailedEvent {
  readonly id: string;
  readonly timestamp: string;
  readonly eventName: 'RawData.JobFailed';
  readonly metadata: EventMetadataBase & { readonly failedReason?: string };
}

export interface RawDataJobCancelledEvent {
  readonly id: string;
  readonly timestamp: string;
  readonly eventName: 'RawData.JobCancelled';
  readonly metadata: EventMetadataBase & { readonly cancelReason?: string };
}

export interface RawDataNoTargetEvent {
  readonly id: string;
  readonly timestamp: string;
  readonly eventName: 'RawData.NoTarget';
  readonly metadata: EventMetadataBase;
}

export type DashboardEvent =
  | RawDataUploadEvent
  | RawDataConversionSucceededEvent
  | RawDataJobFailedEvent
  | RawDataJobCancelledEvent
  | RawDataNoTargetEvent;

/**
 * companyId が社内会社の ID に一致するイベントに isInternal: true を付けて返す。
 * 判別ユニオンを保つため eventName ごとに組み立て直す。
 */
export function tagInternal(
  event: DashboardEvent,
  internalCompanyIds: ReadonlySet<string>,
): DashboardEvent {
  if (!internalCompanyIds.has(event.metadata.companyId)) return event;
  switch (event.eventName) {
    case 'RawDataUpload':
      return { ...event, metadata: { ...event.metadata, isInternal: true } };
    case 'RawDataConversionSucceeded':
      return { ...event, metadata: { ...event.metadata, isInternal: true } };
    case 'RawData.JobFailed':
      return { ...event, metadata: { ...event.metadata, isInternal: true } };
    case 'RawData.JobCancelled':
      return { ...event, metadata: { ...event.metadata, isInternal: true } };
    case 'RawData.NoTarget':
      return { ...event, metadata: { ...event.metadata, isInternal: true } };
  }
}

export type DashboardEventKind =
  | 'received'
  | 'processed'
  | 'failed'
  | 'cancelled'
  | 'noTarget';

export function kindOf(event: DashboardEvent): DashboardEventKind {
  switch (event.eventName) {
    case 'RawDataUpload':
      return 'received';
    case 'RawDataConversionSucceeded':
      return 'processed';
    case 'RawData.JobFailed':
      return 'failed';
    case 'RawData.JobCancelled':
      return 'cancelled';
    case 'RawData.NoTarget':
      return 'noTarget';
  }
}

export function isDashboardEvent(value: unknown): value is DashboardEvent {
  if (value === null || typeof value !== 'object') return false;
  if (!('id' in value) || typeof value.id !== 'string') return false;
  if (!('timestamp' in value) || typeof value.timestamp !== 'string') return false;
  if (!('eventName' in value) || typeof value.eventName !== 'string') return false;
  if (!('metadata' in value)) return false;
  const md = value.metadata;
  if (md === null || typeof md !== 'object') return false;
  if (!('companyId' in md) || typeof md.companyId !== 'string') return false;
  if (!('companyName' in md) || typeof md.companyName !== 'string') return false;
  if (!('rawDataId' in md) || typeof md.rawDataId !== 'string') return false;
  const ev: string = value.eventName;
  if (ev === 'RawDataUpload') {
    return 'documentName' in md && typeof md.documentName === 'string';
  }
  if (ev === 'RawDataConversionSucceeded') return true;
  if (ev === 'RawData.JobFailed') return true;
  if (ev === 'RawData.JobCancelled') return true;
  if (ev === 'RawData.NoTarget') return true;
  return false;
}

/** 課金単位。BE の branch / conversion_process の billing_type と同じ値。 */
export type BillingType = 'per_page' | 'per_case';

/**
 * 確定した raw_data 1 件ぶんのコスト。GET /costs/range が返す。
 * イベントとは別に届くので、クライアントは rawDataId で確定イベントと突き合わせる。
 */
export interface RawDataCost {
  readonly rawDataId: string;
  /** 読み取り時の CP の課金単位 (未設定なら営業所の課金単位)。 */
  readonly billingType: BillingType;
  /** 課金の分母。per_page なら課金対象の枚数、per_case なら課金対象の件数 (case 数)。 */
  readonly units: number;
  /** キャッシュが効いた後の LLM 費用 + 明示キャッシュの保持費用 (USD)。 */
  readonly usd: number;
}

export function isRawDataCost(value: unknown): value is RawDataCost {
  if (value === null || typeof value !== 'object') return false;
  if (!('rawDataId' in value) || typeof value.rawDataId !== 'string') return false;
  if (!('billingType' in value)) return false;
  if (value.billingType !== 'per_page' && value.billingType !== 'per_case') return false;
  if (!('units' in value) || typeof value.units !== 'number') return false;
  return 'usd' in value && typeof value.usd === 'number';
}

export interface EventsMeta {
  readonly count: number;
  readonly firstAt: string | null;
  readonly lastAt: string | null;
}
